import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useTranslation } from 'react-i18next';
import './reports.css';

const VATReports = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [vatData, setVatData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      fetchVATReport();
    }
  }, [dateRange, token, i18n.language]);

  const fetchVATReport = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://naql.nozzm.com/api/vat_report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify({
          start_date: dateRange.startDate,
          end_date: dateRange.endDate
        }),
      });

      const result = await response.json();
      if (result.status) {
        setVatData(result.data || []);
      } else {
        setError(result.message || 'Failed to fetch VAT report');
      }
    } catch (error) {
      setError('Error fetching VAT report');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA');
  };

  const calculateTotals = () => {
    const totals = vatData.reduce((acc, item) => {
      acc.totalSales += parseFloat(item.total_sales) || 0;
      acc.totalVAT += parseFloat(item.total_vat) || 0;
      acc.netSales += parseFloat(item.net_sales) || 0;
      acc.totalInvoices += parseInt(item.invoices_count) || 0;
      return acc;
    }, {
      totalSales: 0,
      totalVAT: 0,
      netSales: 0,
      totalInvoices: 0
    });

    return totals;
  };

  if (loading) {
    return (
      <div className="container tables bg-white mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="container tables bg-white mt-5">
      <div className="tableTitle">
        <h3>{t('reports.vatReports.title')}</h3>
      </div>

      <div className="report-filters">
        <div className="row">
          <div className="col-md-4">
            <label className="form-label">{t('reports.startDate')}</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('reports.endDate')}</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {vatData.length > 0 && (
        <div className="report-summary">
          <div className="row">
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.totalInvoices')}</h5>
                <span className="summary-number">{totals.totalInvoices}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.totalSales')}</h5>
                <span className="summary-number">{formatCurrency(totals.totalSales)}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.totalVAT')}</h5>
                <span className="summary-number">{formatCurrency(totals.totalVAT)}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.netSales')}</h5>
                <span className="summary-number">{formatCurrency(totals.netSales)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>{t('reports.date')}</th>
              <th>{t('reports.invoicesCount')}</th>
              <th>{t('reports.totalSales')}</th>
              <th>{t('reports.totalVAT')}</th>
              <th>{t('reports.netSales')}</th>
              <th>{t('reports.vatRate')}</th>
            </tr>
          </thead>
          <tbody>
            {vatData.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">
                  {t('reports.noVATData')}
                </td>
              </tr>
            ) : (
              vatData.map((item) => (
                <tr key={item.date}>
                  <td>{formatDate(item.date)}</td>
                  <td>{item.invoices_count}</td>
                  <td>{formatCurrency(item.total_sales)}</td>
                  <td>{formatCurrency(item.total_vat)}</td>
                  <td>{formatCurrency(item.net_sales)}</td>
                  <td>15%</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {vatData.length > 0 && (
        <div className="vat-summary">
          <div className="row">
            <div className="col-md-6">
              <div className="summary-section">
                <h4>{t('reports.vatSummary')}</h4>
                <div className="summary-item">
                  <span>{t('reports.totalSales')}:</span>
                  <span>{formatCurrency(totals.totalSales)}</span>
                </div>
                <div className="summary-item">
                  <span>{t('reports.totalVAT')}:</span>
                  <span>{formatCurrency(totals.totalVAT)}</span>
                </div>
                <div className="summary-item total">
                  <span>{t('reports.netSales')}:</span>
                  <span>{formatCurrency(totals.netSales)}</span>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="summary-section">
                <h4>{t('reports.vatCalculation')}</h4>
                <p>{t('reports.vatCalculationText')}</p>
                <div className="calculation">
                  <span>{t('reports.netSales')} = {t('reports.totalSales')} - {t('reports.totalVAT')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VATReports;
